import { Component,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  OnInit, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CopyLegacyFilesService, TaxYearOption } from './copy-legacy-files.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-copy-legacy-files',
    templateUrl: './copy-legacy-files.component.html',
    styleUrls: ['./copy-legacy-files.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CopyLegacyFilesComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  taxYears: TaxYearOption[] = [];
  selectedTaxYear: TaxYearOption | null = null;
  copyingLegacyFiles = false;

  GENERAL_CONFIG: { base_url?: string; admin_api_url?: string; admin?: string } = {};
  USER_SETTINGS: { user?: { sso_id?: string } } = {};
  private isInitialized = false;
  private isLoading = false;

  constructor(
    private copyLegacyFilesService: CopyLegacyFilesService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.cdr.markForCheck();
    this.tryInit();
  }

  private get baseUrl(): string {
    return this.GENERAL_CONFIG?.base_url ?? '';
  }

  private get adminApiUrl(): string {
    return this.GENERAL_CONFIG?.admin_api_url ?? (this.GENERAL_CONFIG as any)?.admin ?? '';
  }

  private get ssoId(): string {
    return this.USER_SETTINGS?.user?.sso_id ?? '';
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.baseUrl || !this.ssoId) return;
    this.isInitialized = true;
    this.loadTaxYears();
  }

  loadTaxYears(): void {
    this.isLoading = true;
    this.cdr.detectChanges();
    this.copyLegacyFilesService.getTaxYears(this.baseUrl, this.ssoId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response?.callSuccess === '1' && Array.isArray(response?.jsonObject)) {
          this.taxYears = response.jsonObject;
        } else {
          this.gtwSnackbarService.openSnackBar(
            response?.errorMessage || 'Something went wrong, please try again!',
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoading = false;
        this.gtwSnackbarService.openSnackBar(
          'Something went wrong, please try again!',
          'gtw-snackbar--red'
        );
        this.cdr.detectChanges();
      },
    });
  }

  moveLegacyFilestoS3(): void {
    const taxYearText = this.selectedTaxYear?.text;
    if (!taxYearText) return;
    if (!this.adminApiUrl) {
      this.gtwSnackbarService.openSnackBar(
        'Admin API URL is not configured.',
        'gtw-snackbar--red'
      );
      return;
    }
    this.copyingLegacyFiles = true;
    this.cdr.detectChanges();
    this.copyLegacyFilesService.moveLegacyFilestoS3(this.adminApiUrl, taxYearText).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response) => {
        this.copyingLegacyFiles = false;
        if (response?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar(
            'Legacy Files Copied Successfully!',
            'gtw-snackbar--green'
          );
        } else {
          this.gtwSnackbarService.openSnackBar(
            response?.errorMessage || 'Something went wrong, please try again!',
            'gtw-snackbar--red'
          );
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.copyingLegacyFiles = false;
        this.gtwSnackbarService.openSnackBar(
          err?.message || 'Something went wrong, please try again!',
          'gtw-snackbar--red'
        );
        this.cdr.detectChanges();
      },
    });
  }
}
